import { type ComponentProps, type ReactNode } from 'react'

import { Badge } from '@/components/common/Badge'
import { ColorIdBorder } from '@/components/common/ColorIdBorder'
import { Mana } from '@/components/common/Mana'
import { Icon } from '@/components/icons/Icon'
import { type DistributiveOmit } from '@/defs/misc'
import { type ColorIdType } from '@/defs/mtg'
import { cn } from '@/utils/commonUtil'

import { ButtonRoot, type Props as RootProps } from '../ButtonRoot/ButtonRoot'

import styles from './ButtonImage.module.scss'

type Props = {
  borderNone?: boolean
  children?: ReactNode
  colorId?: ColorIdType
  colorPrimary?: boolean
  colors?: string[]
  contain?: boolean
  count?: number
  iconProps?: ComponentProps<typeof Icon>
  image?: string
  invert?: boolean
  label?: string
  nowrap?: boolean
  padding?: 'none'
  size?: 'sm' | 'lg'
} & DistributiveOmit<RootProps, 'className'>

export const ButtonImage = ({
  borderNone,
  children,
  colorId,
  colorPrimary,
  colors,
  contain,
  count,
  iconProps,
  image,
  invert,
  label,
  nowrap,
  padding,
  size,
  ...props
}: Props) => (
  <ButtonRoot
    {...props}
    className={cn(styles.container, {
      borderNone,
      colorPrimary,
      contain,
      invert,
      nowrap,
      padding,
      size,
      styles,
    })}
  >
    {colorId && (
      <span className={styles.border}>
        <ColorIdBorder colorId={colorId} vertical />
      </span>
    )}
    <span className={styles.content}>
      {iconProps && (
        <span className={styles.icon}>
          <Icon {...iconProps} />
        </span>
      )}
      {image && (
        <span className={styles.image}>
          <img alt='' decoding='async' loading='lazy' role='presentation' src={image} />
        </span>
      )}
      {colors && (
        <span>
          <Mana colors={colors} />
        </span>
      )}
      <span className={styles.label}>{children}</span>
      {count !== undefined && (
        <span className={styles.count}>
          <Badge count={count}>{label}</Badge>
        </span>
      )}
    </span>
  </ButtonRoot>
)
